/**
 * @author Muhammad Omran
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2020-10-21
 * @description Implementation of the Featured Item Hero Panel
 * @filename fearured-item-panel.tsx
 */

import React, { Suspense } from "react";
import { GraphQLTaggedNode, QueryRenderer } from "react-relay";
import ProgressiveImage from "react-progressive-image";
import { ArticleStory, GRAPHQL_TYPE_ARTICLE_STORY } from "@radivision/graphql/lib/ts/graphql/article-story";
import { BookStory, GRAPHQL_TYPE_BOOK_STORY } from "@radivision/graphql/lib/ts/graphql/book-story";
import { NewsletterStory, GRAPHQL_TYPE_NEWSLETTER_STORY } from "@radivision/graphql/lib/ts/graphql/newsletter-story";
import {
  OnlineCourseStory,
  GRAPHQL_TYPE_ONLINE_COURSE_STORY,
} from "@radivision/graphql/lib/ts/graphql/online-course-story";
import { GRAPHQL_TYPE_PODCAST_STORY, PodcastStory } from "@radivision/graphql/lib/ts/graphql/podcast-story";
import {
  GRAPHQL_TYPE_ORIGINAL_CONTENT_STORY,
  OriginalContentStory,
} from "@radivision/graphql/lib/ts/graphql/original-content-story";
import { GRAPHQL_TYPE_POSTER, Poster } from "@radivision/graphql/lib/ts/graphql/poster";
import { GRAPHQL_TYPE_PERSON, Person } from "@radivision/graphql/lib/ts/graphql/person";
import { GRAPHQL_TYPE_ENTITY, Entity } from "@radivision/graphql/lib/ts/graphql/entity";
import { FixedList, GRAPHQL_TYPE_FIXED_LIST } from "@radivision/graphql/lib/ts/graphql/fixed-list";
import { DynamicList, GRAPHQL_TYPE_DYNAMIC_LIST } from "@radivision/graphql/lib/ts/graphql/dynamic-list";
import { ListCollection, GRAPHQL_TYPE_LIST_COLLECTION } from "@radivision/graphql/lib/ts/graphql/list-collection";
import { ReadyState } from "../../relay/ready-state";
import { FEATURED_ENTITY_QUERY } from "../../relay/queries/featured-item-panel-queries/featured-entity-query";
import { FEATURED_LIST_COLLECTION_QUERY } from "../../relay/queries/featured-item-panel-queries/featured-list-collection-query";
import { FEATURED_LIST_QUERY } from "../../relay/queries/featured-item-panel-queries/featured-list-query";
import { FEATURED_PERSON_QUERY } from "../../relay/queries/featured-item-panel-queries/featured-person-query";
import { POSTER_QUERY } from "../../relay/queries/poster-query";
import { STORY_QUERY } from "../../relay/queries/story-query";
import { OptimizedImageUrls } from "../../component-configuration/optimized-image";
import { PreviewKind } from "@radivision/graphql/lib/ts/graphql/preview-type";
import { ImageHelper } from "../../utilities/image-helper";
import { TopLevelMediaKind } from "@radivision/graphql/lib/ts/graphql/top-level-media-type";
import { Analytics } from "../../utilities/analytics";
import {
  facebookPixelTraceEvent,
  gaEvent,
  snowplowAnalytics,
  getVideoLinksFromMediaItem,
  getCleanUuid,
} from "../../utilities/general";
import { ComponentPropertyValue } from "@radivision/graphql/lib/ts/graphql/component-property-value";
import { ENVIRONMENT } from "../../relay/relay-environment";
import { SVG } from "../../component-configuration/svgs";
import { Show } from "../../component-configuration/show";
import { GeneralItemDetails } from "../../component-configuration/general-item-details";
import { GRAPHQL_TYPE_EVENT } from "@radivision/graphql/lib/ts/graphql/event";
import { Router } from "../../utilities/router";
import { GaEventAction, GaEventPayload } from "../../redux/trackers/types";
import { OpenDetailsPanelAction, OpenDetailsPanelPayload } from "../../redux/details-panel/types";
import { PlayFullScreenVideoPayload, PlayFullScreenVideoAction } from "../../redux/full-screen-video/types";
import * as Actions from "../../redux/actions";
import { AppState } from "../../redux";
import { connect } from "react-redux";
import { Loader } from "../page/loader";
import isEmpty from "lodash.isempty";
import { Preview } from "../../utilities/preview";
import isEqual from "lodash.isequal";
import { extractYoutubeId } from "../../utilities/extract-youtube-id";

const VideoPanel = React.lazy(() => import("../page-framework/video-panel"));

/**
 * static placeholder image
 */
const LOAD = require("../../../static/placeholder.jpg");
const MASTERS = /Entrepreneurial Experts?/i;
/**
 * interface that defines the featured item unified data
 *
 * @interface
 */
interface FeaturedItem {
  /**
   * the hero background
   *
   * @type {OptimizedImageUrls}
   * @memberof FeaturedItem
   */
  backgroundImage?: OptimizedImageUrls;

  /**
   * the call to action button text
   *
   * @type {string}
   * @memberof FeaturedItem
   */
  callToActionText?: string;

  /**
   * the call to action button url
   *
   * @type {string}
   * @memberof FeaturedItem
   */
  callToActionUrl?: string;

  /**
   * item description
   *
   * @type {string}
   * @memberof FeaturedItem
   */
  description?: string;

  /**
   * the id of the story
   *
   * @type {string}
   * @memberof FeaturedItem
   */
  id: string;

  /**
   * item landing page url
   *
   * @type {string}
   * @memberof FeaturedItem
   */
  landingPageUrl?: string;

  /**
   * the logo preview
   *
   * @type {OptimizedImageUrls}
   * @memberof FeaturedItem
   */
  logo: OptimizedImageUrls;

  /**
   * the logo PreviewKind if it's logo > then logo, if person or entity > then headshot, if story > then square
   *
   * @type {PreviewKind}
   * @memberof FeaturedItem
   */
  logoType: PreviewKind;

  /**
   * the revision of the story item
   *
   * @type {string}
   * @memberof FeaturedItem
   */
  revision: string;

  /**
   * featured item title
   *
   * @type {string}
   * @memberof FeaturedItem
   */
  title: string;

  /**
   * item type
   *
   * @type {string}
   * @memberof FeaturedItem
   */
  type: string;

  /**
   *
   *
   * @type {string}
   * @memberof FeaturedItem
   */
  excerptMediaAsset?: string;
}

/**
 * the graph results of the featured item query
 */
type GraphFeaturedItem =
  | ArticleStory
  | BookStory
  | NewsletterStory
  | OnlineCourseStory
  | PodcastStory
  | OriginalContentStory
  | Poster
  | Person
  | Entity
  | FixedList
  | DynamicList
  | ListCollection;

/**
 * interface that defines the props of the Featued Item Panel component
 *
 * @interface
 */
interface FeaturedItemPanelProps {
  /**
   * the featured item object
   *
   * @type {GraphFeaturedItem}
   * @memberof FeaturedItemPanelProps
   */
  featuredItem?: GraphFeaturedItem;

  json?: any;
}

/**
 * interface that defines the state of the component
 *
 * @interface
 */
interface FeaturedItemPanelState {
  /**
   * the featured item in uniform format
   *
   * @type {FeaturedItem}
   * @memberof FeaturedItemPanelState
   */
  item: FeaturedItem;

  /**
   * flag to know if item video is a youtube video or not
   *
   * @type {boolean}
   * @memberof FeaturedItemPanelState
   */
  itemVideoIsYoutube: boolean;

  /**
   * if item is original content > this is the video id to play
   *
   * @type {string}
   * @memberof FeaturedItemPanelState
   */
  itemVideoLink: string;

  /**
   * when click on the call to action button to play the video if any
   *
   * @type {boolean}
   * @memberof FeaturedItemPanelState
   */
  playVideo: boolean;

  videoIsReady: boolean;

  /**
   *
   *
   * @type {number}
   * @memberof FeaturedItemPanelState
   */
  excerptPlayCount?: number;
  userInteraction: boolean;
}

interface MapDispatchProps {
  gaEvent: (payload: GaEventPayload) => GaEventAction;
  openDetailsPanel: (payload: OpenDetailsPanelPayload) => OpenDetailsPanelAction;
  playFullScreenVideo: (p: PlayFullScreenVideoPayload) => PlayFullScreenVideoAction;
}

interface MapStateProps {
  videoId: string | null;
}

class FeaturedItemPanel extends React.Component<
  FeaturedItemPanelProps & MapStateProps & MapDispatchProps,
  FeaturedItemPanelState
> {
  /**
   * flag when the item is a resource
   *
   * @type {boolean}
   */
  isResource: boolean;

  /**
   * class constructor
   *
   * @param props the props of the component
   */
  constructor(props: FeaturedItemPanelProps & MapStateProps & MapDispatchProps) {
    super(props);
    this.isResource = false;
    this.extractFeaturedItem = this.extractFeaturedItem.bind(this);
    this.openDetailsPanel = this.openDetailsPanel.bind(this);
    this.userDidInteract = this.userDidInteract.bind(this);

    const featuredItem: FeaturedItem = props.featuredItem
      ? this.extractFeaturedItem(props.featuredItem)
      : {
          id: getCleanUuid(),
          revision: getCleanUuid(),
          type: "",
          title: "",
          description: "",
          logo: { originalUrl: LOAD, requestedResolutionUrl: LOAD },
          logoType: PreviewKind.LOGO,
        };

    this.state = {
      item: featuredItem,
      itemVideoIsYoutube: false,
      itemVideoLink: "",
      playVideo: false,
      videoIsReady: false,
      excerptPlayCount: 0,
      userInteraction: false,
    };
  }

  /**
   * to extract the featured item details from the graph response
   *
   * @param item the featured item in graph format
   *
   * @returns {FeaturedItem} the featured item in the unified format
   */
  extractFeaturedItem(item: GraphFeaturedItem): FeaturedItem {
    if (!item) return null;
    let description: string = item?.description;
    let link: string;
    let backgroundImage: OptimizedImageUrls = ImageHelper.fetchOptimizedImageUrl({
      imageType: "PREVIEW",
      preview: {
        content: item.previews,
        previewKind: PreviewKind.HERO,
        topLevelMedia: TopLevelMediaKind.IMAGE,
      },
      desiredDimensions: { containerWidthRatio: 1, numberOfItems: 1 },
      revision: item.revision,
    });

    if (!backgroundImage) {
      backgroundImage = ImageHelper.fetchOptimizedImageUrl({
        imageType: "PREVIEW",
        preview: {
          content: item.previews,
          previewKind: PreviewKind.BACKGROUND,
          topLevelMedia: TopLevelMediaKind.IMAGE,
        },
        desiredDimensions: { containerWidthRatio: 1, numberOfItems: 1 },
        revision: item.revision,
      });
    }

    let title: string;
    let logo: OptimizedImageUrls = {};
    let logoType: PreviewKind = PreviewKind.LOGO;
    let backupLogoType: PreviewKind;
    let callToActionText: string;
    let excerptMediaAsset;

    logo = ImageHelper.fetchOptimizedImageUrl({
      imageType: "PREVIEW",
      preview: {
        content: item.previews,
        previewKind: PreviewKind.LOGO,
        topLevelMedia: TopLevelMediaKind.IMAGE,
      },
      desiredDimensions: { containerWidthRatio: 1, numberOfItems: 1 },
      revision: item.revision,
    });

    if (!logo) {
      logo = ImageHelper.fetchOptimizedImageUrl({
        imageType: "PREVIEW",
        preview: {
          content: item.previews,
          previewKind: PreviewKind.HERO_LOGO,
          topLevelMedia: TopLevelMediaKind.IMAGE,
        },
        desiredDimensions: { containerWidthRatio: 1, numberOfItems: 1 },
        revision: item.revision,
      });
    }

    switch (item.__typename) {
      case GRAPHQL_TYPE_POSTER:
      case GRAPHQL_TYPE_FIXED_LIST:
      case GRAPHQL_TYPE_DYNAMIC_LIST:
      case GRAPHQL_TYPE_LIST_COLLECTION:
        backupLogoType = PreviewKind.HERO_LOGO;
        callToActionText = "Play";
        break;
      case GRAPHQL_TYPE_ORIGINAL_CONTENT_STORY:
        backupLogoType = PreviewKind.CAROUSEL;
        callToActionText = "Play";
        break;
      case GRAPHQL_TYPE_ARTICLE_STORY:
      case GRAPHQL_TYPE_BOOK_STORY:
      case GRAPHQL_TYPE_NEWSLETTER_STORY:
      case GRAPHQL_TYPE_ONLINE_COURSE_STORY:
      case GRAPHQL_TYPE_PODCAST_STORY:
        backupLogoType = PreviewKind.SQUARE;
        callToActionText = "Learn More";
        this.isResource = true;
        break;
      case GRAPHQL_TYPE_PERSON:
        backupLogoType = PreviewKind.HEADSHOT;
        callToActionText = "View Profile";
        break;
      case GRAPHQL_TYPE_ENTITY:
        backupLogoType = PreviewKind.LOGO;
        callToActionText = "Visit Company Profile";
        break;
      default:
    }

    if (!logo) {
      logoType = backupLogoType;
      logo = ImageHelper.fetchOptimizedImageUrl({
        imageType: "PREVIEW",
        preview: {
          content: item.previews,
          previewKind: logoType,
          topLevelMedia: TopLevelMediaKind.IMAGE,
        },
        desiredDimensions: { containerWidthRatio: 1, numberOfItems: 5 },
        isSquareImage: logoType === PreviewKind.SQUARE ? true : false,
        revision: item.revision,
      });
    }

    if (item["title"] !== null && item["title"] !== undefined) {
      title = item["title"];
    } else if (item["fullName"] !== null && item["fullName"] !== undefined) {
      title = item["fullName"];
    } else if (item["name"] !== null && item["name"] !== undefined) {
      title = item["name"];
    } else if (item["label"] !== null && item["label"] !== undefined) {
      title = item["label"];
    }

    if (item["responsibility"] !== null && item["responsibility"] !== undefined) {
      description = item["responsibility"];
    }
    if (item["content"] !== null && item["content"] !== undefined) {
      description = item["content"];
    }

    if (item["permalink"] !== null && item["permalink"] !== undefined) {
      link = item["permalink"];
    } else if (item["link"] !== null && item["link"] !== undefined) {
      link = item["link"];
    } else if (item.__typename === GRAPHQL_TYPE_ORIGINAL_CONTENT_STORY) {
      link = item["externalId"];
      if (link && extractYoutubeId(link) === "") {
        const videoSrc = getVideoLinksFromMediaItem(item["mediaItem"]);
        link = videoSrc[0].src;
      } else {
        this.setState({ itemVideoIsYoutube: true });
      }
      this.setState({ itemVideoLink: link });
    } else {
      link = item["landingPageUrl"];
    }

    if (item.previews) {
      excerptMediaAsset = Preview.getUrlFromPreview(item.previews, PreviewKind.EXCERPT);
    }

    return {
      id: item.id,
      revision: item.revision,
      title,
      description,
      backgroundImage,
      logo,
      logoType,
      callToActionText,
      callToActionUrl: link,
      type: item.__typename,
      landingPageUrl: item["landingPageUrl"],
      excerptMediaAsset,
    };
  }

  userDidInteract() {
    this.setState({ userInteraction: true });
  }

  componentDidMount() {
    window.addEventListener("click", this.userDidInteract);
    window.addEventListener("scroll", this.userDidInteract);
  }

  componentWillUnmount() {
    window.removeEventListener("click", this.userDidInteract);
    window.removeEventListener("scroll", this.userDidInteract);
  }

  /**
   * Invoked when component finished re-render after an update
   */
  componentDidUpdate(prevProps: FeaturedItemPanelProps): void {
    if (this.props.featuredItem && !isEqual(prevProps, this.props)) {
      this.setState({
        item: this.extractFeaturedItem(this.props.featuredItem),
      });
    }
  }

  /**
   * A function to open up the details panel.
   *
   * @type {(panelId: string, index: number) => void}
   */
  openDetailsPanel(panelId: string, show: Show, index: number) {
    if (this.state.playVideo) {
      this.setState({ playVideo: false });
    }
    const episode =
      show.items[index].type === GRAPHQL_TYPE_ORIGINAL_CONTENT_STORY || show.items[index].type === GRAPHQL_TYPE_EVENT
        ? index
        : null;

    const type = "General";

    const detailsPanelShow = show.items[index].details as GeneralItemDetails;

    this.props.openDetailsPanel({
      type,
      episode,
      show: detailsPanelShow,
      scrollAnchorId: "heroCarousel",
    });

    Router.getAlias().then((alias: string): void => {
      Router.to(alias, undefined, `${panelId}/${show.id}`);
    });
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    const item = this.state.item;
    const isMasters = this.props.json?.campaign === "MASTERS";
    const [w1, w2, ...wrest] = item?.title?.trim().split(" ");
    const title1 = w1 && w2 ? `${w1} ${w2}` : null;
    const title2 = wrest ? wrest.join(" ") : null;
    const shouldOpenDetailsPanel = !isMasters && this.state.itemVideoLink;
    const playingFullScreenVideo = !isEmpty(this.props.videoId);
    const exerptWasNotPlayed = this.state.excerptPlayCount === 0;
    const shouldPlayBackgroundExcerpt =
      !playingFullScreenVideo && this.state.item.excerptMediaAsset && exerptWasNotPlayed ? true : false;

    const HIDE_DETAILS: boolean = this.props?.json?.hideDetails;

    const unMuted = this.props?.json?.unmute;
    const isMuted = !this.state.userInteraction;
    return (
      <div className="hero-container xxtra featured">
        <div className="hero">
          <div className="featured-item">
            <div className="featured-item-background">
              <ProgressiveImage
                src={
                  this.state.item.backgroundImage
                    ? this.state.item.backgroundImage.requestedResolutionUrl !== undefined &&
                      this.state.item.backgroundImage.requestedResolutionUrl !== null
                      ? this.state.item.backgroundImage.requestedResolutionUrl
                      : this.state.item.backgroundImage.screenResolutionUrl
                    : undefined
                }
                placeholder={
                  this.state.item.backgroundImage ? this.state.item.backgroundImage.placeHolderUrl : undefined
                }
              >
                {(src: any, LOADING: any, srcSetData: any) => (
                  <div className="image-backdrop">
                    <img
                      loading="lazy"
                      alt={`${this.state.item.title ? this.state.item.title : ""} background`}
                      style={{
                        pointerEvents: "none",
                        opacity: !this.state.videoIsReady && this.state.item.backgroundImage ? 1 : 0,
                        transition: "opacity 0.4s cubic-bezier(.665,.235,.265,.8) 0.4s",
                      }}
                      src={src}
                    />
                  </div>
                )}
              </ProgressiveImage>
              {shouldPlayBackgroundExcerpt ? (
                <Suspense fallback={<Loader isActive={!this.state.videoIsReady} />}>
                  <div className="video-backdrop">
                    <VideoPanel
                      videoSrc={this.state.item.excerptMediaAsset}
                      videoPosition="FIT"
                      onReady={() => this.setState({ videoIsReady: true })}
                      mute={!this.state.userInteraction}
                      onVideoEnded={() => {
                        this.setState({ videoIsReady: false, excerptPlayCount: this.state.excerptPlayCount + 1 });
                      }}
                      loop={this.props?.json?.loop}
                    />
                  </div>
                </Suspense>
              ) : null}
            </div>

            {!HIDE_DETAILS ? (
              <div className="featured-item-details">
                {this.state.item.logo ? (
                  <div className={`featured-item-logo ${this.state.item.logoType.toLowerCase()}`}>
                    <ProgressiveImage
                      src={
                        this.state.item.logo
                          ? this.state.item.logo.requestedResolutionUrl !== undefined &&
                            this.state.item.logo.requestedResolutionUrl !== null
                            ? this.state.item.logo.requestedResolutionUrl
                            : this.state.item.logo.screenResolutionUrl
                          : undefined
                      }
                      placeholder={this.state.item.logo ? this.state.item.logo.placeHolderUrl : undefined}
                    >
                      {(src: any, LOADING: any, srcSetData: any) => (
                        <img loading="lazy" alt={`${this.state.item.title ? this.state.item.title : ""}`} src={src} />
                      )}
                    </ProgressiveImage>
                  </div>
                ) : null}
                {!this.isResource ? (
                  <div
                    className={`fetaured-item-title${!this.state.item.logo ? " d-block" : ""} ${isMasters &&
                      "masters"}`}
                  >
                    {isMasters ? (
                      <>
                        <span>{title1}</span>
                        <span>{title2}</span>
                      </>
                    ) : item?.title ? (
                      item?.title
                    ) : (
                      <span className="title-placeholder loading-gradient" />
                    )}
                  </div>
                ) : null}
                <div className="fetaured-item-description">
                  {this.state.item.description ? (
                    this.state.item.description.length > 360 ? (
                      `${this.state.item.description.substr(0, 360 - 1)}...`
                    ) : (
                      this.state.item.description
                    )
                  ) : (
                    <span className="description-placeholder">
                      <span className="loading-gradient" />
                      <span className="loading-gradient" />
                    </span>
                  )}
                </div>
                <div className="slide-actions">
                  {this.state.item.callToActionText ? (
                    <button
                      className="featured-item-button"
                      onClick={() => {
                        Analytics.userHistory({
                          item: this.state.item.title,
                          description: this.state.item.description,
                        });

                        gaEvent(`Featured Content: "${this.state.item.title}"`, "Click", this.state.item.title, true);
                        facebookPixelTraceEvent(event, {
                          category: `Featured Content: "${this.state.item.title}"`,
                          title: `${this.state.item.title ? this.state.item.title : "Carousel Video Played"}`,
                          url: this.state.item.callToActionUrl ? this.state.item.callToActionUrl : window.location.href,
                        });
                        snowplowAnalytics()
                          .then((r) => {
                            if (this.state.itemVideoLink) {
                              r.trackPlayVideo({
                                category: "Featured Content: " + this.state.item.title,
                                label: this.state.item.title ? this.state.item.title : "Featured Video Played",
                                url: this.state.item.callToActionUrl
                                  ? this.state.item.callToActionUrl
                                  : window.location.href,
                              });
                            } else {
                              r.trackClick({
                                category: "Featured Content: " + this.state.item.title,
                                label: this.state.item.title ? this.state.item.title : "Featured Learn More Clicked",
                                url: this.state.item.callToActionUrl
                                  ? this.state.item.callToActionUrl
                                  : window.location.href,
                              });
                            }
                          })
                          .catch((r) => console.debug("SnowPlow not initialized."));

                        if (this.state.itemVideoLink) {
                          this.setState({ playVideo: true });
                          this.props.playFullScreenVideo({ videoId: this.state.itemVideoLink });
                        } else {
                          const url: string = this.state.item.callToActionUrl;
                          const target: "_self" | "_blank" = url.includes("radivision") ? "_self" : "_blank";
                          window.open(url, target);
                        }
                      }}
                    >
                      {this.state.item.callToActionText === "Play" && SVG.play}
                      {this.state.item.callToActionText}
                    </button>
                  ) : null}
                  {shouldOpenDetailsPanel ? (
                    <div
                      className="more-info"
                      onClick={() => {
                        const itemAsShow: Show = {
                          id: this.state.item.id,
                          revision: this.state.item.revision,
                          description: this.state.item.description,
                          items: [
                            {
                              id: this.state.item.id,
                              title: this.state.item.title,
                              type: "Story",
                              details: {
                                id: this.state.item.id,
                                backgroundImage: this.state.item.backgroundImage,
                                logo: this.state.item.logo,
                                title: this.state.item.title,
                                description: this.state.item.description,
                                youTubeVideo: this.state.itemVideoLink,
                              } as GeneralItemDetails,
                            },
                          ],
                        };
                        this.openDetailsPanel(this.state.item.id, itemAsShow, 0);

                        gaEvent(
                          "Hero Slide More info ",
                          "Click",
                          this.state.item.title ? this.state.item.title : "j1iBBlnfvku3C6NGxRav8A",
                          true,
                        );

                        facebookPixelTraceEvent(event, {
                          category: this.state.item.id ? this.state.item.id : null,
                          title: `${this.state.item.title ? this.state.item.title : "Hero Slide More info"}`,
                        });
                      }}
                    >
                      {SVG.info}
                      <span>More Info</span>
                    </div>
                  ) : null}
                  {this.state.item.landingPageUrl &&
                  (this.state.item.landingPageUrl.includes("person") ||
                    this.state.item.landingPageUrl.includes("company")) ? (
                    <a className="save-item" href={this.state.item.landingPageUrl}>
                      {SVG.externalLinkAlt}
                    </a>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
          {this.state.playVideo ? (
            <Suspense fallback={<div>Loading...</div>}>
              <VideoPanel
                youTube={this.state.itemVideoIsYoutube ? this.state.itemVideoLink : null}
                videoSrc={this.state.itemVideoIsYoutube ? null : this.state.itemVideoLink}
                videoPosition="FULL_SCREEN"
                onVideoEnded={() => this.setState({ playVideo: false })}
                hasBackBtn={true}
                loop={this.props?.json?.loop}
              />
            </Suspense>
          ) : null}
          <div className="shadow" />
        </div>
      </div>
    );
  }
}

const mapDispatch = {
  gaEvent: Actions.gaEvent,
  openDetailsPanel: Actions.openDetailsPanel,
  playFullScreenVideo: Actions.playFullScreenVideo,
};

const mapState = (state: AppState) => ({
  user: state.account.user,
  videoId: state.fullScreenVideo.videoId,
});

export const ConnectedFeaturedItemPanel = connect(mapState, mapDispatch)(FeaturedItemPanel);

export class FeaturedItemPanelContainer extends React.Component<ComponentPropertyValue, {}> {
  render() {
    const featuredType: string = Object.keys(this.props).find((key: string, i: number) => key !== "json");
    const itemId: string = this.props[featuredType].id;

    let QUERY: GraphQLTaggedNode;
    switch (featuredType) {
      case "article":
      case "book":
      case "newsletter":
      case "onlineCourse":
      case "podcast":
      case "original-content":
        QUERY = STORY_QUERY;
        break;
      case "person":
        QUERY = FEATURED_PERSON_QUERY;
        break;
      case "entity":
        QUERY = FEATURED_ENTITY_QUERY;
        break;
      case "poster":
        QUERY = POSTER_QUERY;
        break;
      case "dynamicList":
      case "fixedList":
        QUERY = FEATURED_LIST_QUERY;
        break;
      case "listCollection":
        QUERY = FEATURED_LIST_COLLECTION_QUERY;
        break;
      default:
    }

    return (
      <QueryRenderer
        environment={ENVIRONMENT}
        query={QUERY}
        variables={{ id: itemId }}
        render={(readyState: ReadyState<any>): React.ReactElement<any> => {
          let node: React.ReactElement<any>;
          if (readyState.error !== undefined && readyState.error !== null) {
            node = <div className="alert alert-danger about-danger">Error Loading Featured Item!</div>;
          } else if (readyState.props === undefined || readyState.props === null) {
            const props: React.ComponentProps<any> = {
              featuredItem: null,
              json: this.props.json,
            };
            // node = <Loader isActive={true} />;
            node = <ConnectedFeaturedItemPanel {...props} />;
          } else {
            let isNull: boolean = false;
            let graphItem;
            switch (featuredType) {
              case "article":
              case "book":
              case "newsletter":
              case "onlineCourse":
              case "podcast":
              case "original-content":
                if (
                  readyState.props.story !== undefined &&
                  readyState.props.story !== null &&
                  readyState.props.story.node !== undefined &&
                  readyState.props.story.node !== null
                ) {
                  graphItem = readyState.props.story.node;
                } else {
                  isNull = true;
                }
                break;
              case "person":
                if (
                  readyState.props.person !== undefined &&
                  readyState.props.person !== null &&
                  readyState.props.person.person !== undefined &&
                  readyState.props.person.person !== null
                ) {
                  graphItem = readyState.props.person.person;
                } else {
                  isNull = true;
                }
                break;
              case "entity":
                if (
                  readyState.props.entity !== undefined &&
                  readyState.props.entity !== null &&
                  readyState.props.entity.entity !== undefined &&
                  readyState.props.entity.entity !== null
                ) {
                  graphItem = readyState.props.entity.entity;
                } else {
                  isNull = true;
                }
                break;
              case "poster":
                if (
                  readyState.props.poster !== undefined &&
                  readyState.props.poster !== null &&
                  readyState.props.poster.poster !== undefined &&
                  readyState.props.poster.poster !== null
                ) {
                  graphItem = readyState.props.poster.poster;
                } else {
                  isNull = true;
                }
                break;
              case "dynamicList":
              case "fixedList":
                if (
                  readyState.props.list !== undefined &&
                  readyState.props.list !== null &&
                  readyState.props.list.list !== undefined &&
                  readyState.props.list.list !== null
                ) {
                  graphItem = readyState.props.list.list;
                } else {
                  isNull = true;
                }
                break;
              case "listCollection":
                if (
                  readyState.props.listCollection !== undefined &&
                  readyState.props.listCollection !== null &&
                  readyState.props.listCollection.listCollection !== undefined &&
                  readyState.props.listCollection.listCollection !== null
                ) {
                  graphItem = readyState.props.listCollection.listCollection;
                } else {
                  isNull = true;
                }
                break;
              default:
            }
            if (!isNull) {
              const props: React.ComponentProps<any> = {
                featuredItem: graphItem,
                json: this.props.json,
              };
              node = <ConnectedFeaturedItemPanel {...props} />;
            } else {
              node = <div className="alert alert-danger about-danger">Error Loading Featured Item!</div>;
            }
          }

          return node;
        }}
      />
    );
  }
}
